<template>
  <div class="transportation-page">
    <Hero :title="data?.hero?.title" :description="data?.hero?.description">
      <template #image>
        <ElementsImage v-if="data?.hero?.image" v-bind="data.hero.image" loading="eager" />
      </template>
    </Hero>

    <section class="transportation">
      <div class="transportation-sidebar">
        <div class="transportation-sidebar__heading">
          <div class="transportation-sidebar__heading-title">{{ data?.sidebar?.title }}</div>
          <div class="transportation-sidebar__heading-description">
            {{ data?.sidebar?.description }}
          </div>
        </div>
        <div class="transportation-sidebar__list">
          <div v-for="(item, index) in data?.vehicles" :key="index" class="transportation-sidebar__list-item">
            <ElementsImage v-if="item.icon" v-bind="item.icon" />
            <div class="transportation-sidebar__list-item__content">
              <div class="transportation-sidebar__list-item__title">{{ item.title }}</div>
              <div class="transportation-sidebar__list-item__description">{{ item.description }}</div>
            </div>
          </div>
        </div>
      </div>
      <div class="transportation-map" ref="mapContainer" tabindex="-1"></div>
    </section>
  </div>
</template>

<script setup lang="ts">
  import mapboxgl from "mapbox-gl"
  import { polygon } from "@turf/helpers"
  import centroid from "@turf/centroid"
  import "mapbox-gl/dist/mapbox-gl.css"
  import fetchApi from "~/utils/fetchApi"

  const mapContainer = ref<HTMLElement | null>(null)
  let map: mapboxgl.Map | null = null

  const { data: responseData } = await useAsyncData("transportation", () =>
    fetchApi<any>("transportation", {
      wrappedByKey: "data",
      populate: {
        hero: { populate: "*" },
        sidebar: { populate: "*" },
        vehicles: { populate: "*" }
      }
    })
  )

  const data = computed(() => responseData.value || {})

  class ZimmerMarker extends mapboxgl.Marker {
    constructor(options: any, legacyOptions?: any) {
      super(options, legacyOptions)

      const { tags, coordinates } = options
      const icons = {
        logo: `<svg width="1em" height="1em" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 28V27.931L15.9276 6.68966H6.00989V2H24V2.06896L12.1384 23.2758H23.5387V28H4Z" fill="currentColor"/></svg>`,
        bus: `<img src="/images/transportation/icons/bus.png" alt="Bus Icon" style="width: 1em; height: 1em; object-fit: contain; filter: invert(1) brightness(1);">`,
        ferrybot: `<img src="/images/transportation/icons/ferrybot.png" alt="Ferry Icon" style="width: 1em; height: 1em; object-fit: contain; filter: invert(1) brightness(1);">`,
        tramway: `<img src="/images/transportation/icons/tramway.png" alt="Tramway Icon" style="width: 1em; height: 1em; object-fit: contain; filter: invert(1) brightness(1);">`,
        subway: `<img src="/images/transportation/icons/metro.png" alt="Metro Icon" style="width: 1em; height: 1em; object-fit: contain; filter: invert(1) brightness(1);">`
      }

      this.getElement().insertAdjacentHTML(
        "beforeend",
        `
        <div
          style="cursor: pointer; position: absolute; top: 0; width: 100%; aspect-ratio: 1; display: flex; justify-content: center; align-items: center; color: white; font-size: 15px; padding-bottom: 0"
        >
          ${tags.logo ? icons.logo : ""}
          ${tags.bus ? icons.bus : ""}
          ${tags.ferry ? icons.ferrybot : ""}
          ${tags.tram ? icons.tramway : ""}
          ${tags.subway ? icons.subway : ""}
        </div>
      `
      )
      const circle = this.getElement().querySelector("circle")
      if (circle) circle.remove()

      this.getElement().addEventListener("click", () => {
        window.open(`https://www.google.com/maps?q=${coordinates.lat},${coordinates?.lng ?? coordinates?.lon}`, "_blank")
      })
    }
  }

  onMounted(async () => {
    if (!mapContainer.value) return

    mapboxgl.accessToken = "pk.eyJ1IjoiamV3ZWxyeW1hcCIsImEiOiJja3R6ZzZkbHIzZDlnMnVtcGk2bnV6NGUyIn0.ixDy7f2iqj_0Z84D5mSkmA"

    map = new mapboxgl.Map({
      zoom: 14,
      container: mapContainer.value,
      center: [28.984, 41.035],
      style: "mapbox://styles/mapbox/streets-v11"
    })

    try {
      const interpreterData = await fetch("https://overpass.private.coffee/api/interpreter", {
        method: "POST",
        body: data.value?.interpreter || ""
      })
        .then((res) => res.json())
        .catch(() => null)

      if (interpreterData) {
        map.on("load", () => {
          new ZimmerMarker({
            color: "#EA4336",
            tags: { lat: 41.0313842, lng: 28.9888778, logo: true },
            coordinates: { lat: 41.0313842, lon: 28.9888778 }
          })
            .setLngLat([28.9888778, 41.0313842])
            .addTo(map!)

          const locations = interpreterData.elements.map((element: any) => {
            if (element.type === "way") {
              const centerGeom = centroid(
                polygon([
                  element.nodes
                    .map((node: any) => {
                      const elm = interpreterData.elements.find((el: any) => el.type === "node" && el.id === node)
                      return elm ? [elm.lon, elm.lat] : null
                    })
                    .filter((coord: any) => coord !== null)
                ])
              )

              element.lon = centerGeom.geometry.coordinates[0]
              element.lat = centerGeom.geometry.coordinates[1]
            }

            return element
          })

          locations
            .filter((element: any) => element.lon && element.lat && element.tags && element.tags.name)
            .filter(
              (element: any) =>
                (element.tags.tram && element.tags.public_transport === "station") || element.tags.bus || element.tags.ferry || element.tags.subway
            )
            .slice(0, 200)
            .forEach((element: any) => {
              new ZimmerMarker({
                color: "#282828",
                coordinates: {
                  lat: element.lat,
                  lon: element.lon
                },
                tags: element.tags
              })
                .setLngLat([element.lon, element.lat])
                .addTo(map!)
            })
        })
      }
    } catch (e) {
      console.error("Map error:", e)
    }
  })

  const { t } = useI18n()

  useSeoMeta({
    title: () => data.value?.hero?.title || t("menu.transportation"),
    description: () => data.value?.hero?.description || ""
  })
</script>

<style lang="scss" scoped>
  .transportation {
    @apply relative overflow-hidden;
    aspect-ratio: 16/7;
    max-height: 700px;

    &-sidebar {
      @apply flex flex-col gap-9 absolute bg-white/70 h-full py-16 pr-25 overflow-auto z-10 w-1/2;
      padding-left: calc((var(--app-width) - (var(--statu-container-width) - 90px)) / 2);

      &__heading {
        @apply flex flex-col gap-4;
        max-width: 400px;

        &-title {
          @apply font-display text-black uppercase;
          font-size: 24px;
          font-weight: 600;
          line-height: 32px;
          letter-spacing: 0.96px;
        }

        &-description {
          color: #555;
          font-size: 16px;
          font-weight: 400;
          line-height: 22px;
          letter-spacing: 0.32px;
        }
      }

      &__list {
        @apply flex flex-col gap-3;

        &-item {
          @apply text-left flex-none flex gap-4 p-2 bg-transparent border-none justify-start text-black transition-all duration-200;

          :deep(img) {
            @apply w-7 h-7;
            opacity: 0.8;
          }

          &__content {
            display: flex;
            flex-direction: column;
            gap: 2px;
            max-width: 320px;
          }

          &__title {
            flex: none;
            color: #000;
            font-size: 16px;
            font-weight: 500;
            line-height: 20px;
            letter-spacing: 0.32px;
            text-transform: uppercase;
          }

          &__description {
            color: rgba(0, 0, 0, 0.6);
            font-size: 12px;
            font-weight: 500;
            line-height: 18px;
            letter-spacing: 0.1px;
          }
        }
      }
    }

    &-map {
      @apply absolute w-full inset-0 z-0 overflow-hidden isolate;

      :deep(.mapboxgl-canvas) {
        mix-blend-mode: luminosity;
        filter: grayscale(1);
        opacity: 0.4;
      }
    }

    @include media("xl") {
      &-sidebar {
        @apply py-12 pr-20 gap-6 w-[45%];

        &__heading {
          @apply gap-2;

          &-title {
            @apply text-xl;
          }
          &-description {
            @apply text-sm;
          }
        }
        &__list {
          @apply gap-2;

          &-item {
            @apply px-2;

            :deep(img) {
              @apply w-6 h-6;
            }

            &__title {
              @apply text-base;
            }
          }
        }
      }
    }
    @include media("lg") {
      &-sidebar {
        @apply py-10 pr-12 w-1/2 gap-4;

        &__heading {
          @apply gap-1;

          &-title {
            @apply text-lg;
          }
          &-description {
            @apply text-sm;
          }
        }
        &__list {
          &-item {
            @apply px-0;

            :deep(img) {
              @apply w-5 h-5;
            }

            &__title {
              @apply text-sm;
            }
          }
        }
      }
    }
    @include media("md") {
      @apply flex flex-col aspect-square;
      max-height: unset;

      &-sidebar {
        @apply relative p-0 h-fit w-full max-w-full gap-4 py-4 bg-white/80 backdrop-blur-sm;

        &__heading {
          @apply w-full mx-auto px-4;
          max-width: calc(var(--statu-container-width) - var(--statu-container-piece, 3.75rem));
        }
        &__list {
          @apply flex-row w-full overflow-auto mx-auto py-4 -my-4 gap-6;
          padding-left: calc(((var(--app-width) - var(--statu-container-width, var(--app-width)) + var(--statu-container-piece, 0px)) / 2) + 16px);
          padding-right: calc(((var(--app-width) - var(--statu-container-width, var(--app-width)) + var(--statu-container-piece, 0px)) / 2) + 16px);

          &-item {
            @apply px-0.5 gap-3;
          }

          &::-webkit-scrollbar {
            width: 0;
            height: 0;
          }
        }
      }
    }
    @include media("sm") {
      &-sidebar {
        &__heading {
          &-title {
            @apply text-base;
          }
          &-description {
            @apply text-xs;
          }
        }
        &__list {
          &-item {
            &__description {
              @apply hidden;
            }
          }
        }
      }
    }
  }
</style>
